import { Injectable } from '@angular/core';
import { Names } from '@mode-switch/model';
import { BasicChannel, EncChannel, EncField, Encoding } from '@mode-vegas/vegas';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledFormula } from '@mode/shared/util-switch';

@Injectable({
  providedIn: 'root',
})
/**
 * Default handler for drag and drop operations on a cartesian (ie x-y) chart.
 * This implementation makes the same encoding updates regardless of the renderer.
 * It assumes that the primary axis (ie the measures) are stored in 'value' with
 * Measure Values on the primary axis. The secondary axis (ie the dimension) is
 * on either 'column' or 'row' depending on whether the chart is vertical or
 * horizontal.
 */
export class FieldDropHandlerService {
  handleDropOnExistingField(
    encoding: Encoding,
    field: EncField,
    formula: CompiledFormula,
    channel: BasicChannel,
    source: EncChannel | 'field-list' | undefined,
    useMuzeRenderer: boolean,
    zone?: EncodingHelpers.ZoneType
  ) {
    return this.replaceFieldWithFormula(encoding, field, formula, channel, source, zone);
  }

  handleDropOnEmpty(
    view: FlamingoTypes.ModeView | undefined,
    encoding: Encoding,
    chartType: ChartTypes.ChartType,
    formula: CompiledFormula,
    channel: BasicChannel,
    source: EncChannel | 'field-list' | undefined,
    dropIndex: number | undefined,
    useMuzeRenderer: boolean,
    zoneType: EncodingHelpers.ZoneType
  ) {
    const hasColor = this.hasColorColumn(encoding);
    const secondaryFields = this.getSecondaryFields(encoding, chartType);
    if ((channel === 'column' || channel === 'row') && secondaryFields[0] !== undefined) {
      return this.replaceFieldWithFormula(encoding, secondaryFields[0], formula, channel, source, zoneType);
    } else if (channel === 'value' && hasColor && encoding.value[0] !== undefined) {
      return this.replaceFieldWithFormula(
        encoding,
        encoding.value[0],
        formula,
        channel,
        source,
        EncodingHelpers.ZoneType.Aggregate
      );
    } else {
      return this.addFormula(view, encoding, formula, channel, dropIndex, zoneType);
    }
  }

  /**
   * Returns the fields associated with the primary axis. The primary axis is the
   * axis containing the quantitative values (ie measures).
   */
  getPrimaryFields(encoding: Encoding) {
    return encoding.value;
  }

  /**
   * Returns the channel associated with the primary axis. The primary axis is the
   * axis containing the quantitative values (ie measures).
   */
  getPrimaryChannel(): 'row' | 'column' | 'value' {
    return 'value';
  }

  /**
   * Returns the fields associated with the secondary axis and facets. The secondary axis is the
   * axis containing the qualitative values (ie dimensions).
   */
  getSecondaryFields(encoding: Encoding, chartType: ChartTypes.ChartType) {
    const isHorizontalChart = EncodingHelpers.HorizontalChartTypes.has(chartType);

    return isHorizontalChart
      ? encoding.y[0] != null
        ? [encoding.y[0]]
        : []
      : encoding.x[0] != null
        ? [encoding.x[0]]
        : [];
  }

  /**
   * Returns the fields associated with the secondary axis. The secondary axis is the
   * axis containing the qualitative values (ie dimensions).
   */
  getSecondaryAxisFields(encoding: Encoding, chartType: ChartTypes.ChartType) {
    return this.getSecondaryFields(encoding, chartType);
  }

  /**
   * Determines if the encoding has a field (other than Measure Names) on the color channel
   */
  hasColorColumn(encoding: Encoding) {
    return (
      encoding.marks.series.length === 1 &&
      encoding.marks.series[0].properties.color.values[0] != null &&
      encoding.marks.series[0].properties.color.values[0].formula !== Names.Key
    );
  }

  replaceFieldWithFormula(
    encoding: Encoding,
    field: EncField,
    formula: CompiledFormula,
    channel: BasicChannel,
    source: EncChannel | 'field-list' | undefined,
    zone?: EncodingHelpers.ZoneType
  ) {
    let newEncoding;

    if (formula.isField) {
      if (formula.alias === field.id) {
        // inserting field in the same location as it was
        return;
      }
      // move field and dispatch
      newEncoding = EncodingHelpers.replaceAndMoveVegasField(
        encoding,
        formula.alias ?? '',
        channel,
        field.id ?? '',
        undefined,
        formula,
        zone
      );
    } else {
      newEncoding = EncodingHelpers.replaceAndAddNewVegasField(
        encoding,
        formula,
        field.id ?? '',
        channel,
        undefined,
        zone
      );
    }
    if (source === 'color' && newEncoding.encoding.marks.series[0].properties.color.values.length === 0) {
      // Add Measure Names back to color.
      newEncoding = EncodingHelpers.addMeasureNamesToColorChannel(
        newEncoding.encoding,
        newEncoding.encoding.marks.series[0].field
      );
    }

    return newEncoding;
  }

  addFormula(
    view: FlamingoTypes.ModeView | undefined,
    encoding: Encoding,
    formula: CompiledFormula,
    channel: BasicChannel,
    dropIndex: number | undefined,
    zoneType?: EncodingHelpers.ZoneType
  ) {
    if (view == null) {
      return;
    }
    if ((channel === 'column' || channel === 'row') && zoneType == null) {
      zoneType = EncodingHelpers.ZoneType.Discrete;
    }

    let newEncoding = EncodingHelpers.addOrMoveVegasFieldToBasicChannel(
      view,
      encoding,
      channel,
      formula,
      dropIndex,
      undefined,
      zoneType
    );

    if (newEncoding?.encoding.marks.series[0]?.properties.color.values.length === 0) {
      // Add Measure Names back to color.
      newEncoding = EncodingHelpers.addMeasureNamesToColorChannel(
        newEncoding.encoding,
        newEncoding.encoding.marks.series[0].field
      );
    }

    return newEncoding;
  }
}
